import { useEffect, useRef, useState } from 'react';

/**
 * Detects whether a container's content overflows vertically.
 * Optionally accepts a selector to check a descendant element instead of the ref itself
 * (useful when the scroll container is rendered internally by a child component).
 *
 * @param deps - Additional dependencies that should trigger a re-check (e.g. item count)
 * @param innerSelector - CSS selector for a descendant scroll container to observe instead
 */
export function useScrollOverflow(deps: unknown[] = [], innerSelector?: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const wrapper = ref.current;
    if (!wrapper) return;

    const el = (innerSelector ? wrapper.querySelector(innerSelector) : wrapper) ?? wrapper;

    const checkOverflow = () => {
      setIsScrollable(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ref, isScrollable };
}
